// Global Instructions Rule Applied!
//
// Waterfall section (funds Terms tab): the fund's distribution waterfall
// (docs/WATERFALL_CARRY_DESIGN.md, phase 1 - configuration only, nothing
// computes yet). The FeePeriodsEditor pattern: a self-loading FormSection
// (useFundWaterfall -> GET /funds/:id/waterfall), the header fields and one
// row per tier in an antd Table with the house inputs in the cells, and one
// full-replacement save (POST /funds/:id/waterfall -> wf__save_waterfall;
// sequence follows the row order). A fund with no waterfall starts from a
// template (cfg.waterfall_templates, by shape - never per fund; managed on
// Configuration → Waterfall Templates) or empty.
//
// The tier grid, the lookups and the payload the tiers become are
// WaterfallTierGrid, shared with the template editor. Each tier is written as
// two splits: the GP's (rate source + the figure for a fixed rate; "none"
// leaves the GP out) and the LP's, always the remainder. Rates and multiples
// that come from the LP's commitment row (base / stepped carry rate, base /
// stepped hurdle multiple) are sources, not figures: the fund's waterfall is
// one definition and every LP runs it at its own terms - the fund's Carried
// Interest defaults where a commitment states none, printed beside each
// sourced figure. Under the grid each tier is also read back the way the
// agreement states it (owner 2026-10-06: terms in the form LPAs specify them).
//
// Earlier distributions (phase 6): how a new distribution checks the ones
// already made - cfg.waterfall_history_checks, the waterfall's own setting.
// The "?" beside the select explains every option with the description and
// the worked example on its cfg row. A waterfall that names none is run "to
// date" by the engine (wf__definition), and the select shows it that way.
//
// Notes and source reference (header): where the terms come from and how the
// waterfall was set up. A waterfall created from a template starts with the
// template's description as its notes (wf__create_from_template), so that text
// is what the field shows until it is edited.
//
// Read-only once the fund has a booked Distribution (has_booked_distributions).

import React, { useEffect, useMemo, useState } from 'react'
import { Alert, Button, Col, Input, InputNumber, Select, Spin, Tooltip, Typography } from 'antd'
import DatePicker from '../../../../../core/components/DatePicker.tsx'
import { PlusOutlined } from '@ant-design/icons'
import { toast } from 'react-toastify'
import dayjs from 'dayjs'
import { FormSection } from '../../../../../core/components/FormSection.tsx'
import HelpTip from '../../../../../core/components/HelpTip.tsx'
import { showErrorToast } from '../../../../../core/utils/errorToast.tsx'
import { getAccessToken } from '../../../../../core/auth/authService'
import { store } from '../../../../../state_manager/store.ts'
import { decimalToPercent, percentToDecimal } from '../../../../../core/utils/inputNumberFormat.ts'
import { useFundWaterfall } from '../../../hooks/useFundWaterfall.ts'
import type { FundCarryDefaults } from '../../../../../core/utils/fundCarryDefaults.ts'
import WaterfallPoolsGrid from './WaterfallPoolsGrid.tsx'
import WaterfallTierGrid, {
  blankTierRow,
  firstIncompleteTier,
  rowsToTierPayloads,
  tierToRow,
  useWaterfallLookups,
  type TierRow
} from './WaterfallTierGrid.tsx'
import {
  createFundWaterfallFromTemplate,
  createFundWaterfallYearPools,
  moveFundWaterfallTranche,
  saveFundWaterfall
} from '../../../services/fundsService.ts'
import type { FundWaterfallRecord, FundWaterfallSavePayload } from '../../../../../state_manager/data_models/fund_waterfall_record.ts'

const { Text } = Typography

// the Terms tab date format (fundTermsFieldConfigs DATE_FORMAT)
const DATE_FORMAT = 'MM/DD/YYYY'

interface HeaderDraft {
  name: string
  basis_id: number | null
  scope_id: number | null
  history_check_id: number | null
  recallable_pct: number | null
  effective_from: string | null
  source_reference: string | null
  notes: string | null
}

const emptyHeader = (): HeaderDraft => ({
  name: '',
  basis_id: null,
  scope_id: null,
  history_check_id: null,
  recallable_pct: null,
  effective_from: null,
  source_reference: null,
  notes: null
})

interface WaterfallSectionProps {
  fundId: string
  /** the fund's default carry terms (the Terms tab's Carried Interest section): printed beside each commitment-sourced figure */
  fundDefaults?: FundCarryDefaults | null
}

export default function WaterfallSection({ fundId, fundDefaults = null }: WaterfallSectionProps) {
  const lk = useWaterfallLookups()
  const { bases, scopes, templates, lpClassId, gpClassId, remainderSourceId } = lk

  const { waterfall, loading, error, refetch } = useFundWaterfall(fundId)

  const [header, setHeader] = useState<HeaderDraft>(emptyHeader())
  const [rows, setRows] = useState<TierRow[]>([])
  const [started, setStarted] = useState(false)
  const [dirty, setDirty] = useState(false)
  const [saving, setSaving] = useState(false)
  const [creating, setCreating] = useState(false)
  const [templateId, setTemplateId] = useState<string | null>(null)
  const [creatingPools, setCreatingPools] = useState(false)
  const [movingTrancheId, setMovingTrancheId] = useState<string | null>(null)

  const templateOptions = useMemo(
    () => templates.map((t) => ({ value: t.id, label: `${t.name} (${t.tier_count} tier${t.tier_count === 1 ? '' : 's'})` })),
    [templates]
  )

  // ── seed the draft from the record ──
  useEffect(() => {
    const wf = waterfall?.waterfall
    if (!wf) {
      setHeader(emptyHeader())
      setRows([])
      setStarted(false)
      setDirty(false)
      return
    }
    setHeader({
      name: wf.name,
      basis_id: wf.basis_id ?? null,
      scope_id: wf.scope_id,
      history_check_id: wf.history_check_id ?? null,
      recallable_pct: wf.recallable_pct ?? null,
      effective_from: wf.effective_from ?? null,
      source_reference: wf.source_reference ?? null,
      notes: wf.notes ?? null
    })
    setRows((waterfall?.tiers ?? []).map((t) => tierToRow(t, gpClassId, lpClassId)))
    setStarted(true)
    setDirty(false)
  }, [waterfall, gpClassId, lpClassId])

  const locked = waterfall?.has_booked_distributions === true
  // the pools of the saved waterfall (a waterfall is pooled because it has them)
  const pools = waterfall?.pools ?? []
  const hasCrossPoolTier = rows.some(lk.isCrossPool)

  const updateHeader = (patch: Partial<HeaderDraft>) => {
    setHeader((h) => ({ ...h, ...patch }))
    setDirty(true)
  }
  const changeRows = (next: TierRow[]) => {
    setRows(next)
    setDirty(true)
  }
  const addRow = () => changeRows([...rows, blankTierRow(lpClassId)])
  const startEmpty = () => {
    setHeader({ ...emptyHeader(), basis_id: bases[0]?.id ?? null, scope_id: scopes[0]?.id ?? null })
    setRows([])
    setStarted(true)
    setDirty(true)
  }

  const handleCreateFromTemplate = async () => {
    if (!templateId) return
    setCreating(true)
    try {
      const res = await createFundWaterfallFromTemplate(fundId, templateId, getAccessToken(store.getState().auth))
      if (res.error) {
        showErrorToast(res.error)
        return
      }
      toast.success('Waterfall created from the template')
      await refetch()
    } finally {
      setCreating(false)
    }
  }

  // the year pools: a pool per calendar year with capital or an investment,
  // every investment assigned to the pool of its purchase year. It only adds,
  // so it is run again when a new year begins
  const handleCreateYearPools = async () => {
    setCreatingPools(true)
    try {
      const res = await createFundWaterfallYearPools(fundId, getAccessToken(store.getState().auth))
      if (res.error) {
        showErrorToast(res.error)
        return
      }
      const added = res.data?.year_pools
      const n = added?.pools_created ?? 0
      const m = added?.tranches_assigned ?? 0
      toast.success(
        n === 0 && m === 0
          ? 'Year pools: nothing to add'
          : `Year pools: ${n} pool${n === 1 ? '' : 's'} created, ${m} investment${m === 1 ? '' : 's'} assigned`
      )
      await refetch()
    } finally {
      setCreatingPools(false)
    }
  }

  const handleMoveTranche = async (trancheId: string, poolId: string) => {
    setMovingTrancheId(trancheId)
    try {
      const res = await moveFundWaterfallTranche(fundId, trancheId, poolId, getAccessToken(store.getState().auth))
      if (res.error) {
        showErrorToast(res.error)
        return
      }
      toast.success('Investment moved to the pool')
      await refetch()
    } finally {
      setMovingTrancheId(null)
    }
  }

  const handleSave = async () => {
    if (!header.name.trim() || header.basis_id == null || header.scope_id == null) {
      showErrorToast('The waterfall needs a name, a basis and a scope')
      return
    }
    if (rows.length === 0) {
      showErrorToast('Add at least one tier')
      return
    }
    if (gpClassId == null || lpClassId == null || remainderSourceId == null) {
      showErrorToast('The waterfall lookups are not loaded - refresh and try again')
      return
    }
    const invalid = firstIncompleteTier(rows)
    if (invalid > 0) {
      showErrorToast(`Tier ${invalid} needs a type and a stop metric (None for the last tier)`)
      return
    }
    setSaving(true)
    try {
      const payload: FundWaterfallSavePayload = {
        ...(waterfall?.waterfall?.id ? { id: waterfall.waterfall.id } : {}),
        name: header.name.trim(),
        basis_id: header.basis_id,
        scope_id: header.scope_id,
        history_check_id: header.history_check_id ?? lk.defaultHistoryCheckId,
        recallable_pct: header.recallable_pct,
        effective_from: header.effective_from,
        source_reference: header.source_reference,
        notes: header.notes,
        tiers: rowsToTierPayloads(rows, lk, true)
      }
      const res = await saveFundWaterfall(fundId, payload, getAccessToken(store.getState().auth))
      if (res.error) {
        showErrorToast(res.error)
        return
      }
      toast.success('Waterfall saved')
      setDirty(false)
      await refetch()
    } finally {
      setSaving(false)
    }
  }

  // ── chrome ──
  if (!waterfall) {
    return (
      <Col xs={24}>
        <FormSection title='Waterfall' titleColor='purple'>
          {loading ? (
            <div className='flex justify-center py-6'>
              <Spin />
            </div>
          ) : (
            <Alert type='error' showIcon message='Failed to load the waterfall' description={error} />
          )}
        </FormSection>
      </Col>
    )
  }

  const disabled = locked

  return (
    <Col xs={24}>
      <FormSection title='Waterfall' titleColor='purple'>
        {!started ? (
          <div>
            <Text type='secondary'>
              No waterfall yet. Start from the shape the agreement follows — every LP then runs it at the hurdle and
              carry rate on its own commitment — or build the tiers from scratch.
            </Text>
            <div className='flex items-center gap-3 mt-3'>
              <Select
                className='w-96'
                showSearch
                allowClear
                optionFilterProp='label'
                placeholder='Use a template...'
                value={templateId}
                options={templateOptions}
                aria-label='Waterfall template'
                onChange={(v) => setTemplateId(v ?? null)}
              />
              <Button disabled={!templateId} loading={creating} onClick={() => void handleCreateFromTemplate()}>
                Create from template
              </Button>
              <Button type='text' size='small' icon={<PlusOutlined />} onClick={startEmpty}>
                Start empty
              </Button>
            </div>
            {templateId ? (
              <div className='mt-2'>
                <Text type='secondary'>{templates.find((t) => t.id === templateId)?.description}</Text>
              </div>
            ) : null}
          </div>
        ) : (
          <div>
            {locked ? (
              <Alert
                className='mb-3'
                type='info'
                showIcon
                message='Read-only: the fund has a booked distribution'
                description='The tiers were used to compute a booked distribution. Revert that distribution before changing them.'
              />
            ) : null}
            <div className='flex items-start justify-between gap-4 mb-3'>
              <div className='grid grid-cols-2 xl:grid-cols-4 gap-x-3 gap-y-2 flex-1'>
                <div className='xl:col-span-2'>
                  <Text type='secondary'>Name</Text>
                  <Input
                    size='small'
                    value={header.name}
                    disabled={disabled}
                    aria-label='Waterfall name'
                    onChange={(e) => updateHeader({ name: e.target.value })}
                  />
                </div>
                <div>
                  <Text type='secondary'>
                    <Tooltip title='Fund: the stop tests run on the LPs as a group (Origin §6.4(a)). Commitment: each LP is tested on its own contributions and distributions (SBIC, Origin 21, the SPVs).'>
                      Basis
                    </Tooltip>
                  </Text>
                  <Select
                    size='small'
                    className='w-full'
                    value={header.basis_id}
                    options={lk.basisOptions}
                    placeholder='Basis...'
                    disabled={disabled}
                    aria-label='Waterfall basis'
                    onChange={(v) => updateHeader({ basis_id: v })}
                  />
                </div>
                <div>
                  <Text type='secondary'>
                    <Tooltip title='How proceeds are apportioned before the tiers run: by commitment (Whole Fund) or by each investor&apos;s share of the investment realized (Deal-by-Deal, Origin 21).'>
                      Scope
                    </Tooltip>
                  </Text>
                  <Select
                    size='small'
                    className='w-full'
                    value={header.scope_id}
                    options={lk.scopeOptions}
                    placeholder='Scope...'
                    disabled={disabled}
                    aria-label='Waterfall scope'
                    onChange={(v) => updateHeader({ scope_id: v })}
                  />
                </div>
                <div className='xl:col-span-2'>
                  <Text type='secondary'>
                    Earlier distributions
                    <HelpTip title={lk.historyCheckHelp} maxWidth={720} placement='right' />
                  </Text>
                  <Select
                    size='small'
                    className='w-full'
                    value={header.history_check_id ?? lk.defaultHistoryCheckId}
                    options={lk.historyCheckOptions}
                    placeholder='Check...'
                    disabled={disabled}
                    aria-label='Check of earlier distributions'
                    onChange={(v) => updateHeader({ history_check_id: v })}
                  />
                </div>
                <div>
                  <Text type='secondary'>
                    <Tooltip title='Return of capital may be recalled up to this share of commitments (Origin §6.4(h)(ii), SBIC §7.01(e): 120%). Empty = not recallable. Reported only; it does not restore uncalled commitment.'>
                      Recallable
                    </Tooltip>
                  </Text>
                  <InputNumber
                    size='small'
                    className='w-full'
                    value={decimalToPercent(header.recallable_pct)}
                    precision={0}
                    suffix='%'
                    disabled={disabled}
                    aria-label='Recallable percent'
                    onChange={(v) => updateHeader({ recallable_pct: percentToDecimal(v) })}
                  />
                </div>
                <div>
                  <Text type='secondary'>Effective from</Text>
                  <DatePicker
                    size='small'
                    className='w-full'
                    format={DATE_FORMAT}
                    value={header.effective_from ? dayjs(header.effective_from) : null}
                    disabled={disabled}
                    aria-label='Waterfall effective from'
                    onChange={(d) => updateHeader({ effective_from: d ? d.format('YYYY-MM-DD') : null })}
                  />
                </div>
                <div className='xl:col-span-2'>
                  <Text type='secondary'>
                    <Tooltip title='Where the terms come from: the agreement clause (LPA §6.4(a)), or the template the waterfall was created from.'>
                      Source reference
                    </Tooltip>
                  </Text>
                  <Input
                    size='small'
                    value={header.source_reference ?? ''}
                    placeholder='LPA §6.4(a)'
                    disabled={disabled}
                    aria-label='Waterfall source reference'
                    onChange={(e) => updateHeader({ source_reference: e.target.value || null })}
                  />
                </div>
                <div className='xl:col-span-2'>
                  <Text type='secondary'>Notes</Text>
                  <Input.TextArea
                    size='small'
                    autoSize={{ minRows: 2, maxRows: 6 }}
                    value={header.notes ?? ''}
                    disabled={disabled}
                    aria-label='Waterfall notes'
                    onChange={(e) => updateHeader({ notes: e.target.value || null })}
                  />
                </div>
              </div>
              <div className='flex gap-2 pt-5'>
                {/* the year pools: adds only, so it stays available once a distribution is booked */}
                {hasCrossPoolTier || pools.length > 0 ? (
                  <Tooltip
                    title={
                      !waterfall.waterfall?.id || dirty
                        ? 'Save the waterfall first'
                        : 'A pool per calendar year with capital contributed or an investment bought in it. Adds what is missing; nothing that exists is moved.'
                    }
                  >
                    <Button
                      size='small'
                      disabled={!waterfall.waterfall?.id || dirty}
                      loading={creatingPools}
                      onClick={() => void handleCreateYearPools()}
                    >
                      Create year pools
                    </Button>
                  </Tooltip>
                ) : null}
                {disabled ? null : (
                  <>
                    <Button size='small' icon={<PlusOutlined />} onClick={addRow}>
                      Add tier
                    </Button>
                    <Button size='small' type='primary' disabled={!dirty} loading={saving} onClick={() => void handleSave()}>
                      Save waterfall
                    </Button>
                  </>
                )}
              </div>
            </div>
            <WaterfallTierGrid
              rows={rows}
              onChange={changeRows}
              disabled={disabled}
              lookups={lk}
              pools={pools}
              fundDefaults={fundDefaults}
              footer={
                <Text type='secondary'>
                  Rates and multiples that read &ldquo;commitment …&rdquo; come from each LP&apos;s commitment terms
                  (base / stepped carry rate and hurdle), the fund&apos;s Carried Interest terms where a commitment
                  states none - so a side-letter LP runs the same tiers at its own figures and every other LP at
                  the fund&apos;s.
                </Text>
              }
            />
            {rows.length > 0 ? (
              pools.length > 0 ? (
                <WaterfallPoolsGrid
                  pools={pools}
                  gaps={waterfall.pool_gaps}
                  asOf={waterfall.pools_as_of}
                  locked={locked}
                  movingTrancheId={movingTrancheId}
                  onMove={(trancheId, poolId) => void handleMoveTranche(trancheId, poolId)}
                />
              ) : hasCrossPoolTier && waterfall.waterfall?.id ? (
                <Alert
                  className='mt-3'
                  type='info'
                  showIcon
                  message='No pools yet'
                  description='This waterfall offsets losses across pools. Create year pools makes a pool for every calendar year with capital contributed or an investment bought in it; until then a distribution cannot be computed.'
                />
              ) : null
            ) : null}
          </div>
        )}
      </FormSection>
    </Col>
  )
}

export type { FundWaterfallRecord }
